import { L, locale } from '@/i18n';

let counter = 0;

export function uid(prefix = 'id'): string {
  counter = (counter + 1) % 1_000_000;
  return `${prefix}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}${counter.toString(36)}`;
}

export const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
export const clamp01 = (v: number) => clamp(v, 0, 1);
export const round = (v: number, digits = 0) => {
  const f = 10 ** digits;
  return Math.round(v * f) / f;
};
export const sum = (xs: number[]) => xs.reduce((a, b) => a + b, 0);
export const mean = (xs: number[]) => (xs.length ? sum(xs) / xs.length : 0);
export const gcd = (a: number, b: number): number => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) [a, b] = [b, a % b];
  return a || 1;
};
export const lcm = (a: number, b: number) => Math.abs(a * b) / gcd(a, b);

export function groupBy<T, K extends string>(items: T[], key: (t: T) => K): Record<K, T[]> {
  const out = {} as Record<K, T[]>;
  for (const it of items) (out[key(it)] ||= []).push(it);
  return out;
}

export function uniq<T>(items: T[]): T[] {
  return Array.from(new Set(items));
}

/* ---------- time ---------- */
export const DAY = 86_400_000;
export const HOUR = 3_600_000;
export const MINUTE = 60_000;

let clockOffset = 0;
let frozen: number | null = null;
/** Central clock — overridable in tests and for demo simulation. */
export const clock = {
  now: () => (frozen ?? Date.now()) + clockOffset,
  freeze: (t: number | null) => {
    frozen = t;
  },
  setOffset: (ms: number) => {
    clockOffset = ms;
  },
};

export function dayKey(t: number): string {
  const d = new Date(t);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function startOfDay(t: number): number {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

export function relTime(t: number, now = clock.now()): string {
  const diff = t - now;
  const abs = Math.abs(diff);
  if (abs < MINUTE) return diff < 0 ? L('just now', 'akkurat nå') : L('now', 'nå');
  let n: number;
  let unit: [string, string, string, string];
  if (abs < HOUR) { n = Math.round(abs / MINUTE); unit = ['min', 'min', 'min', 'min']; }
  else if (abs < DAY) { n = Math.round(abs / HOUR); unit = ['hour', 'hours', 'time', 'timer']; }
  else { n = Math.round(abs / DAY); unit = ['day', 'days', 'dag', 'dager']; }
  const s = `${n} ${n === 1 ? L(unit[0], unit[2]) : L(unit[1], unit[3])}`;
  return diff < 0 ? L(`${s} ago`, `for ${s} siden`) : L(`in ${s}`, `om ${s}`);
}

export function fmtDate(t: number, opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }): string {
  return new Date(t).toLocaleDateString(locale(), opts);
}

export function fmtTime(t: number): string {
  return new Date(t).toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit' });
}

/* ---------- text ---------- */

/** Fold case + diacritics for search; keeps Norwegian letters searchable both ways. */
export function fold(s: string): string {
  return s
    .toLowerCase()
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/ł/g, 'l');
}

/** Normalise free-text answers: trim, collapse whitespace, strip trailing punctuation. */
export function normalizeAnswer(s: string, caseSensitive = false): string {
  let out = s.trim().replace(/\s+/g, ' ').replace(/[.!?;:]+$/g, '').replace(/[’`]/g, "'");
  if (!caseSensitive) out = out.toLowerCase();
  return out;
}

/** Normalise but also ignore diacritics — used to give "accent" feedback. */
export function looseAnswer(s: string): string {
  return fold(normalizeAnswer(s)).replace(/[^a-z0-9' ]/g, '');
}

export function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (!m) return n;
  if (!n) return m;
  let prev = Array.from({ length: n + 1 }, (_, i) => i);
  for (let i = 1; i <= m; i++) {
    const cur = [i];
    for (let j = 1; j <= n; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = cur;
  }
  return prev[n];
}

export function tokenize(s: string): string[] {
  return fold(s)
    .split(/[^a-z0-9]+/)
    .filter((t) => t.length > 1);
}

export function plural(n: number, word: string, pluralWord = `${word}s`): string {
  return `${n} ${n === 1 ? word : pluralWord}`;
}

export function pct(v: number): string {
  return `${Math.round(v * 100)}%`;
}
